// Snapping and smart guides (DRAW_PLAN §5). Candidates: document grid, pixel grid, anchor points, path
// outlines, guides, artboard edges / centres, and other objects' bounding-box edges / centres. A snap happens
// within 6 screen px; the closest wins, and on a tie the higher priority (points → paths → guides → objects →
// artboards → grid). Smart guides report what lined up (alignment lines, equal spacing, angles) for drawing.
// Pure: built once per gesture from the display list, then queried every pointer move.
import type { Rect, Subpath } from '../model/types';
import { nearest } from './pathOps';

export type SnapKind = 'point' | 'path' | 'guide' | 'object' | 'artboard' | 'grid' | 'pixel';
const PRIORITY: Record<SnapKind, number> = { point: 0, path: 1, guide: 2, object: 3, artboard: 4, grid: 5, pixel: 6 };

export type SnapOptions = {
  grid: boolean;
  gridSpacing: number; // pt
  pixel: boolean;
  pixelSize: number; // pt per pixel at the document's raster DPI
  points: boolean;
  paths: boolean;
  guides: boolean;
  artboards: boolean;
  smart: boolean;
};
export const DEFAULT_SNAP: SnapOptions = {
  grid: false,
  gridSpacing: 18,
  pixel: false,
  pixelSize: 1,
  points: true,
  paths: true,
  guides: true,
  artboards: true,
  smart: true,
};

/** A line a coordinate can snap to (x = const or y = const), with where it came from (for the guide line). */
export type Line = { v: number; kind: SnapKind; from: number; to: number };
export type SnapSource = {
  /** Vertical lines (x values) and horizontal lines (y values). */
  xs: Line[];
  ys: Line[];
  points: [number, number][];
  paths: Subpath[][];
  /** Other objects' boxes, for equal spacing. */
  boxes: Rect[];
};

export type Guide =
  | { kind: 'line'; axis: 'x' | 'y'; v: number; from: number; to: number }
  | { kind: 'spacing'; axis: 'x' | 'y'; gaps: [number, number][]; at: number; size: number }
  | { kind: 'angle'; x: number; y: number; deg: number }
  | { kind: 'point'; x: number; y: number };

/** Builds the candidates from objects (excluding the ones being moved), artboards and guides. */
export function buildSource(
  objects: { gbounds: Rect; subpaths?: Subpath[] }[],
  artboards: Rect[],
  guides: { axis: 'x' | 'y'; pos: number }[],
  o: SnapOptions,
): SnapSource {
  const xs: Line[] = [],
    ys: Line[] = [];
  const points: [number, number][] = [];
  const paths: Subpath[][] = [];
  const boxes: Rect[] = [];
  const edges = (r: Rect, kind: SnapKind) => {
    for (const v of [r.x, r.x + r.w / 2, r.x + r.w]) xs.push({ v, kind, from: r.y, to: r.y + r.h });
    for (const v of [r.y, r.y + r.h / 2, r.y + r.h]) ys.push({ v, kind, from: r.x, to: r.x + r.w });
  };
  for (const ob of objects) {
    if (o.smart) edges(ob.gbounds, 'object');
    boxes.push(ob.gbounds);
    if (o.points && ob.subpaths) for (const sp of ob.subpaths) for (const p of sp.points) points.push([p.x, p.y]);
    if (o.paths && ob.subpaths) paths.push(ob.subpaths);
  }
  if (o.artboards) for (const a of artboards) edges(a, 'artboard');
  if (o.guides)
    for (const g of guides) (g.axis === 'x' ? xs : ys).push({ v: g.pos, kind: 'guide', from: -1e9, to: 1e9 });
  return { xs, ys, points, paths, boxes };
}

type Pick = { d: number; v: number; kind: SnapKind; line?: Line };
const better = (a: Pick | null, b: Pick) => !a || b.d < a.d - 1e-9 || (Math.abs(b.d - a.d) <= 1e-9 && PRIORITY[b.kind] < PRIORITY[a.kind]);

function snapAxis(values: number[], lines: Line[], o: SnapOptions, tol: number): { delta: number; pick: Pick | null } {
  let best: Pick | null = null;
  let bestDelta = 0;
  for (const v of values) {
    for (const l of lines) {
      const d = Math.abs(l.v - v);
      if (d <= tol) {
        const p = { d, v: l.v, kind: l.kind, line: l };
        if (better(best, p)) {
          best = p;
          bestDelta = l.v - v;
        }
      }
    }
    if (o.grid) {
      const g = Math.round(v / o.gridSpacing) * o.gridSpacing;
      const p = { d: Math.abs(g - v), v: g, kind: 'grid' as const };
      if (p.d <= tol && better(best, p)) {
        best = p;
        bestDelta = g - v;
      }
    }
    if (o.pixel) {
      const g = Math.round(v / o.pixelSize) * o.pixelSize;
      const p = { d: Math.abs(g - v), v: g, kind: 'pixel' as const };
      if (p.d <= tol && better(best, p)) {
        best = p;
        bestDelta = g - v;
      }
    }
  }
  return { delta: bestDelta, pick: best };
}

/**
 * Snaps a moving box (its left / centre / right and top / middle / bottom) by (dx, dy) to the candidates and,
 * with smart guides, to equal spacing between neighbours. Returns the corrected move and the guides to draw.
 * `tol` is in world units (6 screen px / zoom).
 */
export function snapBox(box: Rect, dx: number, dy: number, src: SnapSource, o: SnapOptions, tol: number): { dx: number; dy: number; guides: Guide[] } {
  const b = { x: box.x + dx, y: box.y + dy, w: box.w, h: box.h };
  const sx = snapAxis([b.x, b.x + b.w / 2, b.x + b.w], src.xs, o, tol);
  const sy = snapAxis([b.y, b.y + b.h / 2, b.y + b.h], src.ys, o, tol);
  let ex = sx.delta,
    ey = sy.delta;
  const guides: Guide[] = [];
  let spacingX: Guide | null = null,
    spacingY: Guide | null = null;
  if (o.smart) {
    const sp = equalSpacing(b, src.boxes, tol);
    if (sp.x && (!sx.pick || Math.abs(sp.x.delta) < sx.pick.d)) {
      ex = sp.x.delta;
      spacingX = sp.x.guide;
    }
    if (sp.y && (!sy.pick || Math.abs(sp.y.delta) < sy.pick.d)) {
      ey = sp.y.delta;
      spacingY = sp.y.guide;
    }
  }
  const nb = { x: b.x + ex, y: b.y + ey, w: b.w, h: b.h };
  if (spacingX) guides.push(spacingX);
  else if (sx.pick) guides.push(lineGuide('x', sx.pick, nb));
  if (spacingY) guides.push(spacingY);
  else if (sy.pick) guides.push(lineGuide('y', sy.pick, nb));
  return { dx: dx + ex, dy: dy + ey, guides };
}

function lineGuide(axis: 'x' | 'y', p: Pick, nb: Rect): Guide {
  const l = p.line;
  const [a0, a1] = axis === 'x' ? [nb.y, nb.y + nb.h] : [nb.x, nb.x + nb.w];
  return { kind: 'line', axis, v: p.v, from: Math.min(a0, l ? Math.max(l.from, -1e6) : a0), to: Math.max(a1, l ? Math.min(l.to, 1e6) : a1) };
}

/** Equal gaps: the box between two neighbours on a row (or column) snaps so both gaps match, or next to a
 *  pair of neighbours so its gap repeats theirs. */
function equalSpacing(b: Rect, boxes: Rect[], tol: number) {
  const out: { x?: { delta: number; guide: Guide }; y?: { delta: number; guide: Guide } } = {};
  for (const axis of ['x', 'y'] as const) {
    const lo = (r: Rect) => (axis === 'x' ? r.x : r.y),
      size = (r: Rect) => (axis === 'x' ? r.w : r.h),
      hi = (r: Rect) => lo(r) + size(r);
    const overlap = (r: Rect) => (axis === 'x' ? r.y < b.y + b.h && r.y + r.h > b.y : r.x < b.x + b.w && r.x + r.w > b.x);
    const row = boxes.filter(overlap);
    const before = row.filter((r) => hi(r) <= lo(b) + tol).sort((p, q) => hi(q) - hi(p));
    const after = row.filter((r) => lo(r) >= hi(b) - tol).sort((p, q) => lo(p) - lo(q));
    let best: { delta: number; guide: Guide } | null = null;
    const consider = (delta: number, gaps: [number, number][]) => {
      if (Math.abs(delta) <= tol && (!best || Math.abs(delta) < Math.abs(best.delta))) {
        const at = axis === 'x' ? b.y + b.h / 2 : b.x + b.w / 2;
        best = { delta, guide: { kind: 'spacing', axis, gaps, at, size: gaps[0]![1] - gaps[0]![0] } };
      }
    };
    const L = before[0],
      R = after[0];
    if (L && R) {
      // Centre between the two: gap left = gap right.
      const target = (hi(L) + lo(R) - size(b)) / 2;
      const d = target - lo(b);
      const g = target - hi(L);
      consider(d, [
        [hi(L), hi(L) + g],
        [target + size(b), target + size(b) + g],
      ]);
    }
    if (L && before[1]) {
      const gap = lo(L) - hi(before[1]);
      const target = hi(L) + gap;
      consider(target - lo(b), [
        [hi(before[1]), lo(L)],
        [hi(L), target],
      ]);
    }
    if (R && after[1]) {
      const gap = lo(after[1]) - hi(R);
      const target = lo(R) - gap - size(b);
      consider(target - lo(b), [
        [target + size(b), lo(R)],
        [hi(R), lo(after[1])],
      ]);
    }
    if (best) out[axis] = best;
  }
  return out;
}

/** Snaps a single point (pen, shape corners, anchors): points first, then path outlines, then lines. */
export function snapPoint(x: number, y: number, src: SnapSource, o: SnapOptions, tol: number): { x: number; y: number; guides: Guide[] } {
  if (o.points) {
    let best: [number, number] | null = null,
      bd = tol;
    for (const p of src.points) {
      const d = Math.hypot(p[0] - x, p[1] - y);
      if (d <= bd) {
        bd = d;
        best = p;
      }
    }
    if (best) return { x: best[0], y: best[1], guides: [{ kind: 'point', x: best[0], y: best[1] }] };
  }
  const sx = snapAxis([x], src.xs, o, tol);
  const sy = snapAxis([y], src.ys, o, tol);
  if (o.paths && !sx.pick && !sy.pick) {
    let best: { x: number; y: number; d: number } | null = null;
    for (const sps of src.paths) {
      const h = nearest(sps, x, y);
      if (h && h.dist <= tol && (!best || h.dist < best.d)) best = { x: h.x, y: h.y, d: h.dist };
    }
    if (best) return { x: best.x, y: best.y, guides: [{ kind: 'point', x: best.x, y: best.y }] };
  }
  const nx = x + sx.delta,
    ny = y + sy.delta;
  const guides: Guide[] = [];
  const pt = { x: nx, y: ny, w: 0, h: 0 };
  if (sx.pick) guides.push(lineGuide('x', sx.pick, pt));
  if (sy.pick) guides.push(lineGuide('y', sy.pick, pt));
  return { x: nx, y: ny, guides };
}

/** Smart-guide angles: a drag from (x0, y0) within 2° of 0/45/90/… snaps onto that angle. */
export function snapAngle(x0: number, y0: number, x: number, y: number, o: SnapOptions, degTol = 2): { x: number; y: number; guides: Guide[] } {
  if (!o.smart) return { x, y, guides: [] };
  const dx = x - x0,
    dy = y - y0;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return { x, y, guides: [] };
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  const target = Math.round(deg / 45) * 45;
  if (Math.abs(deg - target) > degTol) return { x, y, guides: [] };
  const a = (target * Math.PI) / 180;
  return { x: x0 + len * Math.cos(a), y: y0 + len * Math.sin(a), guides: [{ kind: 'angle', x: x0, y: y0, deg: ((-target % 360) + 360) % 360 }] };
}
